import * as React from 'react'

import { spacing, Text } from 'main/ui'
import RangeSlider, { IRangeProps } from './RangeSlider'
import { formatMonthlyBudgetFromDollars, formatWeeklyBudgetFromDollars } from './util'

interface IProps {
  minValue: number
  maxValue: number
  step?: number
  value: IRangeProps
  monthlySchedule: boolean
  weeklySchedule: boolean
  subsidizedCareAccepted: boolean
  subsidyName: string
  onChange: (value: IRangeProps) => void
  perMonthLabel?: string
  perWeekLabel?: string
  subsidyLabel?: string
}

export default function BudgetRangeSlider(props: IProps) {
  const {
    minValue,
    maxValue,
    step = 15,
    value,
    monthlySchedule,
    weeklySchedule,
    subsidizedCareAccepted,
    subsidyName,
    onChange,
    perMonthLabel,
    perWeekLabel,
    subsidyLabel,
  } = props

  return (
    <div>
      <div
        style={{
          height: `${spacing.majorScale(6)}px`,
          marginBottom: `${spacing.majorScale(2)}px`,
        }}
      >
        <RangeLabel
          value={value}
          monthlySchedule={monthlySchedule}
          weeklySchedule={weeklySchedule}
          subsidizedCareAccepted={subsidizedCareAccepted}
          subsidyName={subsidyName}
          perMonthLabel={perMonthLabel}
          perWeekLabel={perWeekLabel}
          subsidyLabel={subsidyLabel}
        />
      </div>
      <div
        style={{
          marginTop: `${spacing.majorScale(2)}px`,
          marginBottom: `${spacing.majorScale(2)}px`,
          marginLeft: `${spacing.minorScale(1)}px`,
          marginRight: `${spacing.minorScale(-4)}px`,
        }}
      >
        <RangeSlider
          range={{ min: minValue, max: maxValue }}
          step={step}
          value={value}
          onChange={onChange}
        />
      </div>
    </div>
  )
}

const RangeLabel = (props: {
  value: IRangeProps
  monthlySchedule: boolean
  weeklySchedule: boolean
  subsidizedCareAccepted: boolean
  subsidyName: string
  perMonthLabel?: string
  perWeekLabel?: string
  subsidyLabel?: string
}) => {
  const {
    value: { min, max },
    monthlySchedule: monthlySchedule,
    weeklySchedule: weeklySchedule,
    subsidizedCareAccepted: subsidizedCareAccepted,
    subsidyName: subsidyName,
    perMonthLabel: perMonthLabel,
    perWeekLabel: perWeekLabel,
    subsidyLabel: subsidyLabel,
  } = props

  if (subsidizedCareAccepted) {
    return (
      <div>
        <MonthlyLabel min={min} max={max} />
        <SubsidizedCareLabel fontSize={14} muted subsidyName={subsidyName} label={subsidyLabel} />
      </div>
    )
  }

  if (!monthlySchedule && weeklySchedule) {
    return <WeeklyLabel min={min} max={max} label={perWeekLabel} />
  }

  if (monthlySchedule && !weeklySchedule) {
    return <MonthlyLabel min={min} max={max} label={perMonthLabel} />
  }

  return (
    <div>
      <MonthlyLabel min={min} max={max} label={perMonthLabel} />
      <WeeklyLabel min={min} max={max} fontSize={14} label={perWeekLabel} muted />
    </div>
  )
}

const WeeklyLabel = (props: {
  min: number
  max: number
  fontSize?: number
  muted?: boolean
  label?: string
}) => {
  const { min: min, max: max, fontSize: fontSize, muted: muted, label: label } = props
  return (
    <div>
      <Text fontSize={fontSize} muted={muted}>
        {formatWeeklyBudgetFromDollars(min, max)} - {formatWeeklyBudgetFromDollars(max, max)}{' '}
        {label ? label : '/week'}
      </Text>
    </div>
  )
}

const MonthlyLabel = (props: {
  min: number
  max: number
  fontSize?: number
  muted?: boolean
  label?: string
}) => {
  const { min: min, max: max, fontSize: fontSize, muted: muted, label: label } = props
  return (
    <div>
      <Text fontSize={fontSize} muted={muted}>
        {formatMonthlyBudgetFromDollars(min, max)} - {formatMonthlyBudgetFromDollars(max, max)}{' '}
        {label ? label : '/month'}
      </Text>
    </div>
  )
}

const SubsidizedCareLabel = (props: {
  fontSize?: number
  muted?: boolean
  subsidyName?: string
  label?: string
}) => {
  const { fontSize: fontSize, muted: muted, subsidyName: subsidyName, label: label } = props
  return (
    <div>
      <Text fontSize={fontSize} muted={muted}>
        {subsidyName && subsidyName.charAt(0).toUpperCase() + subsidyName.slice(1)}{' '}
        {label ? label : 'will be charged monthly'}
      </Text>
    </div>
  )
}
